import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { Marker, Polygon, Polyline } from '@react-google-maps/api';

const containerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = { lat: 20.5937, lng: 78.9629 };

const RenderPins = ({ pins }) => {
  if (!pins?.length) return null;

  return pins.map((pin, index) => (
    <Marker
      key={index}
      position={{
        lat: pin.coordinate.latitude,
        lng: pin.coordinate.longitude,
      }}
      title={pin.displayName}
      icon={{
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: pin.hexColor,
        fillOpacity: 1,
        strokeWeight: 1,
      }}
    />
  ));
};

const RenderPolygons = ({ polygons }) => {
  if (!polygons) return null;

  return Object.values(polygons)
    .filter(group => Array.isArray(group?.coords) && group.coords.length > 2)
    .map((group, index) => {
      const paths = group.coords
        .filter(p => typeof p.lat === 'number' && typeof p.lng === 'number')
        .map(({ lat, lng }) => ({ lat, lng }));

      if (paths.length < 3) return null;

      return (
        <Polygon
          key={index}
          paths={paths}
          options={{
            fillColor: group?.hexColor || '#2196F3',
            fillOpacity: 0.4,
            strokeColor: group?.hexColor || '#2196F3',
            strokeOpacity: 0.8,
            strokeWeight: 2,
          }}
        />
      );
    });
};

const RenderLines = ({ lines }) => {
  if (!lines) return null;

  return Object.values(lines)
    .filter(group => Array.isArray(group?.coords) && group.coords.length > 1)
    .map((group, index) => {
      const path = group.coords
        .filter(p => typeof p.lat === 'number' && typeof p.lng === 'number')
        .map(({ lat, lng }) => ({ lat, lng }));

      // Hide invalid polylines
      if (path.length < 2) return null;

      return (
        <Polyline
          key={index}
          path={path}
          options={{
            strokeColor: group?.hexColor || '#FF0000',
            strokeOpacity: 1,
            strokeWeight: 3,
          }}
        />
      );
    });
};

function WeighbridgeMap({ mapData, show }) {
  const mapRef = useRef(null);
  const [center] = useState(defaultCenter);
  const [zoom] = useState(15);
  const [isLoading, setIsLoading] = useState(true);

  const onLoad = map => {
    mapRef.current = map;
  };

  useEffect(() => {
    if (!show || !window.google || !mapRef.current) return;

    console.log('map data ===', mapData);

    const hasPins = mapData?.pins?.length > 0;
    const hasPolygons = mapData?.polygons && Object.keys(mapData.polygons).length > 0;
    const hasLines = mapData?.lines && Object.keys(mapData.lines).length > 0;
    const hasMapData = hasPins || hasPolygons || hasLines;

    // Map data exists
    if (hasMapData) {
      let centerLat = 0;
      let centerLng = 0;
      let count = 0;

      if (hasPins) {
        mapData.pins.forEach(pin => {
          centerLat += pin.coordinate.latitude;
          centerLng += pin.coordinate.longitude;
          count++;
        });
      }

      if (hasPolygons) {
        Object.values(mapData.polygons).forEach(group => {
          if (group.coords) {
            group.coords.forEach(({ lat, lng }) => {
              centerLat += lat;
              centerLng += lng;
              count++;
            });
          }
        });
      }

      if (hasLines) {
        Object.values(mapData.lines).forEach(group => {
          if (group.coords) {
            group.coords.forEach(({ lat, lng }) => {
              centerLat += lat;
              centerLng += lng;
              count++;
            });
          }
        });
      }

      if (count > 0) {
        const center = {
          lat: centerLat / count,
          lng: centerLng / count,
        };
        mapRef.current.panTo(center);
        mapRef.current.setZoom(15);
        setIsLoading(false);
      }
    } else {
      // No map data show user location
      console.log('No map data, getting user location');
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            console.log('position ===', position);
            const userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            mapRef.current.panTo(userLocation);
            mapRef.current.setZoom(15);
            setIsLoading(false);
          },
          error => {
            console.log('Geolocation error:', error);
            mapRef.current.panTo(defaultCenter);
            mapRef.current.setZoom(15);
            setIsLoading(false);
          },
        );
      } else {
        setIsLoading(false);
      }
    }
  }, [mapData, show]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* ✅ Loader overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
          <div
            style={{
              textAlign: 'center',
            }}>
            <div
              style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 10px',
              }}
            />
            <p style={{ color: '#666', margin: 0 }}>Loading map...</p>
          </div>
        </div>
      )}

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        onLoad={onLoad}
        options={{
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          clickableIcons: true,
          zoomControl: true,
          mapTypeId: 'hybrid',
          optimized: true,
          styles: [],
        }}>
        <RenderPins pins={mapData?.pins} />
        <RenderPolygons polygons={mapData?.polygons} />
        <RenderLines lines={mapData?.lines} />
      </GoogleMap>

      {/* ✅ Add CSS for spinner animation */}
      <style>{`
				@keyframes spin {
					0% { transform: rotate(0deg); }
					100% { transform: rotate(360deg); }
				}
			`}</style>
    </div>
  );
}

export default WeighbridgeMap;
